import { useState, useEffect } from "react";
import { PageHeader } from "@/components/Primitives";
import CrudResource from "@/components/CrudResource";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import api from "@/lib/api";
import MarketsZonesManager from "@/components/markets/MarketsZonesManager";
import UnzonedAudit from "@/components/markets/UnzonedAudit";
import BoundaryReview from "@/components/markets/BoundaryReview";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, Package } from "lucide-react";

// Inventory status by fill (sold + reserved) / total.
function fillStatus(pct) {
  if (pct >= 100) return { key: "full", label: "Full", color: "#FE421C", bg: "bg-[#FE421C]/12 text-[#C42607]" };
  if (pct >= 85) return { key: "nearly", label: "Nearly full", color: "#F97316", bg: "bg-[#F97316]/12 text-[#C2410C]" };
  if (pct >= 60) return { key: "filling", label: "Filling up", color: "#F59E0B", bg: "bg-[#F59E0B]/12 text-[#B45309]" };
  return { key: "healthy", label: "Availability", color: "#9ACC32", bg: "bg-[#9ACC32]/12 text-[#5B880C]" };
}

function Bar({ sold, reserved, total }) {
  const soldPct = Math.min(100, (sold / total) * 100);
  const resPct = Math.min(100 - soldPct, (reserved / total) * 100);
  return (
    <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden flex mt-3">
      <div className="h-full bg-[#0094A8]" style={{ width: `${soldPct}%` }} title={`${sold} sold`} />
      <div className="h-full bg-[#F59E0B]" style={{ width: `${resPct}%` }} title={`${reserved} reserved`} />
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-lg font-bold font-display tnum" style={{ color: color || "#1E2025" }}>{value}</p>
    </div>
  );
}

function Inventory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/publication_inventory").then((r) => setRows(r.data.items)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(3)].map((_, i) => <div key={i} className="h-44 cc-card animate-pulse" />)}</div>;
  if (rows.length === 0) return <p className="text-sm text-slate-400">No publication inventory configured.</p>;

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((p) => {
        const total = p.total_positions || 32;
        const sold = p.sold_positions || 0;
        const reserved = p.reserved_positions || 0;
        const rawAvail = total - sold - reserved;
        const available = Math.max(0, rawAvail);
        const fillPct = Math.min(100, Math.round(((sold + reserved) / total) * 100));
        const s = fillStatus(fillPct);
        return (
          <div key={p.id} className="cc-card cc-card-hover p-5" data-testid={`inv-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-display font-semibold text-slate-800 truncate">{p.zone}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{p.month || "Current issue"}{p.ad_type ? ` · ${p.ad_type}` : ""}</p>
              </div>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold ${s.bg}`} data-testid={`inv-status-${p.id}`}>
                <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />{s.label}
              </span>
            </div>

            <Bar sold={sold} reserved={reserved} total={total} />
            <p className="text-xs text-slate-400 mt-2">{fillPct}% filled</p>

            <div className="mt-4 grid grid-cols-4 gap-2">
              <Stat label="Total" value={total} />
              <Stat label="Sold" value={sold} color="#0094A8" />
              <Stat label="Reserved" value={reserved} color="#B45309" />
              <Stat label="Available" value={available} color="#5B880C" />
            </div>

            {rawAvail < 0 && (
              <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-[#FE421C]/10 text-[#C42607] px-2.5 py-1.5 text-[11px] font-medium" data-testid={`inv-warning-${p.id}`}>
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                Inventory inconsistency: sold + reserved exceeds total by {Math.abs(rawAvail)}. Please review.
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function TerritoriesZones() {
  const { user } = useAuth();
  const isAdmin = ["tenant_owner", "platform_owner"].includes(user?.role);
  return (
    <div data-testid="page-territories">
      <PageHeader title="Markets & Zones" subtitle="The single authoritative Market → Zone structure for advertisers, leads, contracts and sales territories." />
      <Tabs defaultValue="markets">
        <TabsList>
          <TabsTrigger value="markets" data-testid="tab-markets">Markets &amp; Zones</TabsTrigger>
          {isAdmin && <TabsTrigger value="boundaries" data-testid="tab-boundaries">Boundary Review</TabsTrigger>}
          {isAdmin && <TabsTrigger value="unzoned" data-testid="tab-unzoned">Zone Audit</TabsTrigger>}
          <TabsTrigger value="inventory" data-testid="tab-inventory">Publication Inventory</TabsTrigger>
          <TabsTrigger value="products">Products</TabsTrigger>
        </TabsList>
        <TabsContent value="markets"><MarketsZonesManager /></TabsContent>
        {isAdmin && <TabsContent value="boundaries"><BoundaryReview /></TabsContent>}
        {isAdmin && <TabsContent value="unzoned"><UnzonedAudit /></TabsContent>}
        <TabsContent value="inventory"><Inventory /></TabsContent>
        <TabsContent value="products"><CrudResource resourceKey="products" /></TabsContent>
      </Tabs>
    </div>
  );
}
